import { computed } from "vue";
import store from "@/store";
import {
  createAsyncState,
  fetchAsyncState,
  resetAsyncState
} from "@/composables/AsyncState";
import { buscarAcessoOrganograma } from "../services/organograma.service";
import type {
  AcessoOrganograma,
  NivelOrganograma
} from "../types/organograma.types";

/*
 * Acesso do usuário logado pelo organograma da API, no lugar do `codNivel`.
 * Estado no nível do módulo: quem consome (menu, guard e telas do Painel do
 * Consultor) lê a mesma resposta, carregada uma vez por usuário. Não usa
 * inject, então serve também fora de `setup`.
 */
const SEM_ACESSO: AcessoOrganograma = { perfil: null, telas: [] };

const estado = createAsyncState<AcessoOrganograma>(SEM_ACESSO, {
  shallow: true
});

let carregadoPara: number | null = null;
let emAndamento: Promise<void> | null = null;

/**
 * Carrega uma vez por usuário. A carga em andamento é reaproveitada e, depois
 * de falha, chamar de novo tenta outra vez.
 */
const carregar = (): Promise<void> => {
  const codUsuario = Number(store.getters.usuarioLogado?.codUsuario) || 0;

  if (codUsuario !== carregadoPara) {
    resetAsyncState(estado, SEM_ACESSO);
    carregadoPara = codUsuario;
    emAndamento = null;
  }

  if (estado.successfully) return Promise.resolve();

  emAndamento ??= fetchAsyncState(estado, buscarAcessoOrganograma).finally(
    () => {
      emAndamento = null;
    }
  );

  return emAndamento;
};

const perfil = computed(() => estado.data.perfil);

/** Sem a resposta da API, nenhuma. */
const telaLiberada = (tela: NivelOrganograma): boolean =>
  estado.data.telas.includes(tela);

export function useAcessoOrganograma() {
  return { estado, carregar, perfil, telaLiberada };
}
